import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, PaletteIcon, SearchIcon, MousePointerClickIcon, ExternalLinkIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
const voorbeelden = [{
  title: 'Installatiebedrijf De Vries',
  branche: 'Bouw & installatie',
  image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop',
  tags: ['WordPress', 'SEO', 'Responsive']
}, {
  title: 'Restaurant Het Kaasplein',
  branche: 'Horeca',
  image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800&h=500&fit=crop',
  tags: ['WordPress', 'Reserveringen', 'Fotografie']
}, {
  title: 'Advocatenkantoor Bakker & Zoon',
  branche: 'Zakelijke dienstverlening',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
  tags: ['Maatwerk', 'SEO', 'Conversie']
}, {
  title: 'Fysiotherapie Alkmaar Centrum',
  branche: 'Zorg',
  image: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&h=500&fit=crop',
  tags: ['WordPress', 'Online afspraken', 'SEO']
}, {
  title: 'Bloemenwinkel Flora',
  branche: 'Retail',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=500&fit=crop',
  tags: ['Webshop', 'WordPress', 'Responsive']
}, {
  title: 'Accountantskantoor Noordzij',
  branche: 'Zakelijke dienstverlening',
  image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=500&fit=crop',
  tags: ['Maatwerk', 'Portaal', 'SEO']
}];
export function WebsiteVoorbeeldenPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website voorbeelden | Inspiratie voor je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Inspirerende website voorbeelden per sector. Bekijk professionele websites voor MKB, ZZP, horeca, zorg en meer. Ontdek wat een goede bedrijfswebsite kenmerkt.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website voorbeelden | Inspiratie voor je website | Noord',
      'og:description': 'Inspirerende website voorbeelden per sector. Bekijk professionele websites voor MKB, ZZP, horeca, zorg en meer.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-voorbeelden',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    const handleScroll = () => {
      const hero = document.querySelector('.hero');
      if (hero) {
        const rect = hero.getBoundingClientRect();
        if (rect.top <= 0 && rect.bottom >= 0) {
          hero.classList.add('active');
        } else {
          hero.classList.remove('active');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-3059">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-3060">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3061">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-3062" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-3063" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-3064" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3065">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-3066">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-3067">
              Home
            </a>
            <span className="text-navy-500" data-id="element-3068">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-3069">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-3070">/</span>
            <span className="text-accent-light font-medium" data-id="element-3071">
              Website voorbeelden
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-3072">
            Inspirerende website voorbeelden
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-3073">
            Bekijk voorbeelden van moderne bedrijfswebsites die wij hebben
            gebouwd. Laat je inspireren voor jouw eigen website.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-3074">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-3075">
              Eigen website laten maken
              <ArrowRightIcon className="w-5 h-5" data-id="element-3076" />
            </a>
          </div>
        </div>
      </section>

      {/* Voorbeelden grid */}
      <section className="py-24 bg-surface-muted" data-id="element-3077">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3078">
          <div className="text-center mb-16" data-id="element-3079">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3080">
              Website voorbeelden per branche
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3081">
              Elke website is uniek ontworpen voor de specifieke doelgroep en
              branche van de klant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3082">
            {voorbeelden.map(item => <div key={item.title} className="group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-medium transition-all duration-300" data-id="element-3083">
                <div className="aspect-[16/10] overflow-hidden relative" data-id="element-3084">
                  <img src={item.image} alt={`Website voorbeeld: ${item.title}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-3085" />
                  <div className="absolute inset-0 bg-navy-900/0 group-hover:bg-navy-900/40 transition-colors duration-300 flex items-center justify-center" data-id="element-3086">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-navy-900 font-semibold px-4 py-2 rounded-button text-sm flex items-center gap-2" data-id="element-3087">
                      Bekijk voorbeeld
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-3088" />
                    </span>
                  </div>
                </div>
                <div className="p-6" data-id="element-3089">
                  <p className="text-xs font-semibold text-accent uppercase tracking-wider mb-2" data-id="element-3090">
                    {item.branche}
                  </p>
                  <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-3091">
                    {item.title}
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-3092">
                    {item.tags.map(tag => <span key={tag} className="text-xs bg-navy-50 text-navy-600 px-2.5 py-1 rounded-full font-medium" data-id="element-3093">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Wat maakt een goede website */}
      <section className="py-24 bg-white" data-id="element-3094">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3095">
          <div className="text-center mb-16" data-id="element-3096">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3097">
              Wat maakt een goede website?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3098">
              Een succesvolle website combineert drie essentiële elementen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3099">
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-3100">
              <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-3101">
                <PaletteIcon className="w-7 h-7 text-accent" data-id="element-3102" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3103">Design</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3104">
                Een professioneel en modern ontwerp dat vertrouwen wekt en jouw
                merkidentiteit versterkt. Eerste indrukken tellen.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-3105">
              <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-3106">
                <SearchIcon className="w-7 h-7 text-accent" data-id="element-3107" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3108">SEO</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3109">
                Technische optimalisatie en slimme content zodat jouw website
                goed vindbaar is in Google voor relevante zoekwoorden.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-3110">
              <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-3111">
                <MousePointerClickIcon className="w-7 h-7 text-accent" data-id="element-3112" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3113">
                Conversie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3114">
                Strategische call-to-actions en gebruiksvriendelijke navigatie
                die bezoekers omzetten in klanten en aanvragen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-b border-navy-100" data-id="element-3115">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3116">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-3117">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3118">
            <a href="#/website-laten-maken" className="inline-flex items-center gap-1.5 text-accent hover:text-accent-hover font-medium underline underline-offset-4 transition-colors" data-id="element-3119">
              Website laten maken
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3120" />
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3121">•</span>
            <a href="#/wordpress-websites" className="inline-flex items-center gap-1.5 text-accent hover:text-accent-hover font-medium underline underline-offset-4 transition-colors" data-id="element-3122">
              WordPress websites
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3123" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3124" />
    </main>;
}